import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal, RotateCcw, X, AlertTriangle, Check, PlayCircle } from 'lucide-react';
import { ApiClientError, DataTable, EmptyState, Money, Spinner, StatusBadge, type Column } from '@riskpay/ui-components';
import type { Money as MoneyType } from '@riskpay/types';
import { api } from '../lib/client';

/* ==========================================================================
 *  Settlement engine + UTR reconciliation
 *
 *  Running a batch moves real money, so the trigger is a two-step confirm and
 *  the result is reported per merchant — including failures, which never abort
 *  the rest of the batch. Reconciliation is the second half: treasury pastes
 *  the bank UTR against each settlement, and only then does it become PAID.
 * ========================================================================== */

interface SettlementRow {
  settlement_ref: string;
  batch_id: string;
  cycle: string;
  gross_amount: string;
  net_payout: string;
  reserve_held: string;
  currency: string;
  transaction_count: number;
  status: string;
  utr: string | null;
  rail: string | null;
  failure_reason: string | null;
  scheduled_for: string;
  paid_at: string | null;
  merchant_ref: string;
  display_name: string;
}

interface BatchResult {
  batch_id: string;
  settled_count: number;
  failed_count: number;
  total_net: { amount: string; formatted: string };
  settlements: {
    settlement_id: string;
    merchant_ref: string;
    transactions: number;
    net: { formatted: string };
    reserve_held: { formatted: string };
  }[];
  failures: { merchantId: string; error: string }[];
}

/**
 * Close a settlement out by hand — paid against a bank UTR, or failed.
 *
 * Failing one is not a status change: it reverses the journal and releases the
 * transactions so the next batch settles them again. The merchant is still
 * owed the money, so it goes back to pending rather than evaporating.
 */
function UtrCell({ settlement }: { settlement: SettlementRow }) {
  const queryClient = useQueryClient();
  const [utr, setUtr] = useState('');
  const [failing, setFailing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const done = () => {
    setError(null);
    setFailing(false);
    setRetrying(false);
    setReason('');
    void queryClient.invalidateQueries({ queryKey: ['settlements'] });
    // Reversing a settlement hands its payments back to the queue, so the queue
    // is stale too. Without this the operator reverses a batch and is told
    // nothing is owed, while the money is sitting there waiting.
    void queryClient.invalidateQueries({ queryKey: ['settlement-queue'] });
  };

  const reconcile = useMutation({
    mutationFn: () => api.post(`/settlements/${settlement.settlement_ref}/reconcile`, { status: 'PAID', utr }),
    onSuccess: done,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Reconciliation failed.'),
  });

  const fail = useMutation({
    mutationFn: () =>
      api.post(`/settlements/${settlement.settlement_ref}/reconcile`, { status: 'FAILED', reason: reason.trim() }),
    onSuccess: done,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not reverse it.'),
  });

  const retry = useMutation({
    mutationFn: () =>
      api.post<{ created: boolean; settlement_ref?: string; message: string }>(
        `/settlements/${settlement.settlement_ref}/retry`,
        { reason: reason.trim() },
      ),
    onSuccess: done,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not retry it.'),
  });

  if (settlement.utr) return <span className="numeral text-xs text-content-muted">{settlement.utr}</span>;

  /*
   * A failed settlement used to render an em dash and nothing else, so the
   * only thing an operator could do about a payout that bounced was wait and
   * hope a scheduled batch picked the money up. Retrying cuts a fresh
   * settlement from the released payments, which is the only safe way to do
   * it — the failed row keeps its history instead of being edited into
   * looking successful.
   */
  if (settlement.status === 'FAILED' || settlement.status === 'REVERSED') {
    if (retrying) {
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What was fixed?"
            className="input h-8 w-44 text-2xs"
            aria-label={`Reason for retrying ${settlement.settlement_ref}`}
          />
          <button
            type="button"
            onClick={() => retry.mutate()}
            disabled={reason.trim().length < 10 || retry.isPending}
            className="btn btn-secondary btn-sm h-8 px-2 text-2xs"
          >
            {retry.isPending ? <Spinner className="h-3 w-3" /> : 'Retry'}
          </button>
          <button type="button" onClick={() => setRetrying(false)} className="btn btn-ghost btn-sm h-8 px-1.5 text-2xs">
            Cancel
          </button>
          {error && <span className="text-2xs text-danger">{error}</span>}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <span className="text-2xs text-content-faint">no transfer</span>
        <button
          type="button"
          onClick={() => { setRetrying(true); setError(null); }}
          className="btn btn-ghost btn-sm h-8 px-1.5 text-2xs text-content-subtle hover:text-brand-400"
          title="Cut a fresh settlement for this merchant and try the transfer again"
        >
          <RotateCcw className="h-3 w-3" aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }

  if (settlement.status !== 'PENDING' && settlement.status !== 'PROCESSING') {
    return <span className="text-2xs text-content-faint">—</span>;
  }

  if (failing) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why the bank rejected it"
          className="input h-8 w-48 text-2xs"
          aria-label={`Failure reason for ${settlement.settlement_ref}`}
        />
        <button
          type="button"
          onClick={() => fail.mutate()}
          disabled={reason.trim().length < 10 || fail.isPending}
          className="btn btn-sm h-8 border-danger-ring bg-danger-soft px-2 text-2xs text-danger-text"
        >
          {fail.isPending ? <Spinner className="h-3 w-3" /> : 'Reverse'}
        </button>
        <button type="button" onClick={() => setFailing(false)} className="btn btn-ghost btn-sm h-8 px-1.5 text-2xs">
          Cancel
        </button>
        {error && <span className="text-2xs text-danger">{error}</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        value={utr}
        onChange={(e) => setUtr(e.target.value.toUpperCase())}
        placeholder="Bank UTR"
        className="input numeral h-8 w-36 text-2xs"
        aria-label={`UTR for ${settlement.settlement_ref}`}
      />
      <button
        type="button"
        onClick={() => reconcile.mutate()}
        disabled={utr.trim().length < 6 || reconcile.isPending}
        className="btn btn-secondary btn-sm h-8 px-2 text-2xs"
        title="Mark paid against this UTR"
      >
        {reconcile.isPending ? <Spinner className="h-3 w-3" /> : <Check className="h-3 w-3" />}
      </button>
      <button
        type="button"
        onClick={() => { setFailing(true); setError(null); }}
        className="btn btn-ghost btn-sm h-8 px-1.5 text-2xs text-content-subtle hover:text-danger"
        title="Reverse: return the money to pending and re-settle later"
      >
        <X className="h-3 w-3" />
      </button>
      {error && <span className="text-2xs text-danger">{error}</span>}
    </div>
  );
}

/**
 * How much is sitting in each state, before anyone scrolls.
 *
 * Settlements here are paid by hand, from a banking portal. The first question
 * of the morning is "how much am I transferring and to how many people" — and
 * the answer used to require counting rows.
 */
function OutstandingWork({ summary }: { summary?: Record<string, { count: number; amount: MoneyType }> }) {
  const cells = [
    { key: 'pending', label: 'Awaiting transfer', tone: 'text-warning-text' },
    { key: 'processing', label: 'In progress', tone: 'text-content' },
    { key: 'failed', label: 'Failed', tone: 'text-danger-text' },
    { key: 'paid', label: 'Paid', tone: 'text-content' },
  ] as const;

  return (
    <section className="panel grid grid-cols-2 divide-x divide-y divide-line sm:grid-cols-4 sm:divide-y-0">
      {cells.map((c) => {
        const s = summary?.[c.key];
        return (
          <div key={c.key} className="px-5 py-4">
            <p className="meta">{c.label}</p>
            <p className={`numeral mt-1.5 text-2xl font-semibold tracking-tight ${c.tone}`}>
              {(s?.count ?? 0).toLocaleString('en-IN')}
            </p>
            <p className="mt-1 text-xs text-content-subtle">
              <Money amount={s?.amount.amount} className="text-content-muted" />
            </p>
          </div>
        );
      })}
    </section>
  );
}

/**
 * Settling part of what a merchant is owed.
 *
 * "Settle now" pays out everything waiting, which is the common case and the
 * only one the panel used to offer. The rest of the time an operator is
 * answering a specific request — release two lakh today, send it by IMPS
 * because it is urgent — and that needs a figure, a rail, and a look at what
 * the figure actually buys before any money moves.
 */
function CustomSettle({
  merchantId,
  merchantName,
  reason,
  reasonTooShort,
  onDone,
}: {
  merchantId: string;
  merchantName: string;
  reason: string;
  reasonTooShort: boolean;
  onDone: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [rail, setRail] = useState('NEFT');
  const [error, setError] = useState<string | null>(null);

  const payload = () => ({
    merchant_id: merchantId,
    reason: reason.trim(),
    rail,
    ...(amount.trim() ? { amount: amount.trim() } : {}),
  });

  const preview = useMutation({
    mutationFn: () =>
      api.post<{
        transaction_count: number;
        gross: MoneyType;
        net_payout: MoneyType;
        reserve_held: MoneyType;
        left_behind: { count: number; amount: MoneyType };
        capped: boolean;
        message: string;
      }>('/settlements/preview', payload()),
    onSuccess: () => setError(null),
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not work that out.'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ created: boolean; settlement_ref?: string; message: string }>('/settlements/custom', payload()),
    onSuccess: (r) => {
      setError(null);
      setOpen(false);
      setAmount('');
      preview.reset();
      onDone(r.created ? `Created ${r.settlement_ref}. ${r.message}` : r.message);
      void queryClient.invalidateQueries({ queryKey: ['settlements'] });
      void queryClient.invalidateQueries({ queryKey: ['settlement-queue'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'That settlement could not be created.'),
  });

  // A malformed amount is caught here rather than by the server, so the
  // operator is not told "invalid request" about a field they can see.
  const amountValid = amount.trim() === '' || /^\d{1,12}(\.\d{1,2})?$/.test(amount.trim());
  const seen = preview.data;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setError(null); }}
        className="btn btn-ghost h-8 text-xs text-content-subtle"
        title={`Settle part of what ${merchantName} is owed`}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        Custom
      </button>
    );
  }

  return (
    <div className="basis-full rounded border border-line bg-surface-sunken p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor={`amt-${merchantId}`} className="mb-1 block text-2xs uppercase tracking-wider text-content-subtle">
            Up to (₹)
          </label>
          <input
            id={`amt-${merchantId}`}
            value={amount}
            onChange={(e) => { setAmount(e.target.value); preview.reset(); }}
            inputMode="decimal"
            placeholder="all of it"
            className="input numeral h-8 w-32 text-xs"
          />
        </div>
        <div>
          <label htmlFor={`rail-${merchantId}`} className="mb-1 block text-2xs uppercase tracking-wider text-content-subtle">
            Rail
          </label>
          <select
            id={`rail-${merchantId}`}
            value={rail}
            onChange={(e) => setRail(e.target.value)}
            className="input h-8 w-24 text-xs"
          >
            <option value="NEFT">NEFT</option>
            <option value="IMPS">IMPS</option>
            <option value="RTGS">RTGS</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => preview.mutate()}
          disabled={!amountValid || preview.isPending}
          className="btn btn-secondary h-8 text-xs"
        >
          {preview.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
          Check
        </button>
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={reasonTooShort || !amountValid || create.isPending || !seen || seen.transaction_count === 0}
          title={
            reasonTooShort
              ? 'Write a reason first'
              : !seen
                ? 'Check what this covers first'
                : seen.transaction_count === 0
                  ? 'Nothing matches these terms'
                  : undefined
          }
          className="btn btn-primary h-8 text-xs"
        >
          {create.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
          Create settlement
        </button>
        <button type="button" onClick={() => { setOpen(false); preview.reset(); setError(null); }} className="btn btn-ghost h-8 text-xs">
          Cancel
        </button>
      </div>

      {!amountValid && (
        <p className="mt-2 text-2xs text-danger">Rupees and paise only — for example 200000 or 1999.50.</p>
      )}

      {/*
        The operator is about to move this money by hand, so the figure they
        are agreeing to is spelled out before the button does anything —
        including what is being left behind, which is the part a limit makes
        easy to forget.
      */}
      {seen && (
        <div className="mt-2.5 border-t border-line-subtle pt-2.5 text-2xs">
          <p className="text-content-muted">{seen.message}</p>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-content-subtle">
            <span>
              Net payout <Money amount={seen.net_payout.amount} className="text-brand-400" />
            </span>
            <span>
              Reserve held <Money amount={seen.reserve_held.amount} className="text-content-muted" />
            </span>
            {seen.capped && (
              <span className="text-warning-text">
                Leaving {seen.left_behind.count} payment{seen.left_behind.count === 1 ? '' : 's'} ·{' '}
                <Money amount={seen.left_behind.amount.amount} className="text-warning-text" />
              </span>
            )}
          </p>
        </div>
      )}

      {error && <p role="alert" className="mt-2 text-2xs text-danger">{error}</p>}
    </div>
  );
}

/**
 * Money captured and owed, but not yet in any settlement.
 *
 * This is the thing the screen was missing. The table below lists settlements
 * that already exist; nothing told an operator what was *waiting* to become
 * one — so the panel looked empty on exactly the mornings there was most to do.
 *
 * Each row can be cut on its own, off-cycle, which is what an urgent merchant
 * request actually looks like.
 */
function WaitingToSettle() {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['settlement-queue'],
    queryFn: () => api.get<{
      count: number;
      total: MoneyType;
      data: { merchant_id: string; merchant_ref: string; display_name?: string; legal_name?: string;
              txn_count: number; net: string; settlement_cycle: string }[];
    }>('/settlements/queue'),
    refetchInterval: 60_000,
  });

  const settleOne = useMutation({
    mutationFn: (merchantId: string) =>
      api.post<{ created: boolean; settlement_ref?: string; message: string }>('/settlements/on-demand', {
        merchant_id: merchantId,
        reason: reason.trim(),
      }),
    onSuccess: (r) => {
      setError(null);
      setNote(r.created ? `Created ${r.settlement_ref}. ${r.message}` : r.message);
      void queryClient.invalidateQueries({ queryKey: ['settlements'] });
      void queryClient.invalidateQueries({ queryKey: ['settlement-queue'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'That settlement could not be created.'),
  });

  const reasonTooShort = reason.trim().length < 10;

  return (
    <section className="panel">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-3.5">
        <h2 className="text-base font-medium">Waiting to be settled</h2>
        <p className="text-xs text-content-subtle">
          {isLoading ? (
            ' '
          ) : (data?.count ?? 0) === 0 ? (
            'Nothing owed right now'
          ) : (
            <>
              <span className="numeral text-content-muted">{data?.count}</span> merchant
              {data?.count === 1 ? '' : 's'} ·{' '}
              <Money amount={data?.total.amount} className="text-content-muted" />
            </>
          )}
        </p>
      </header>

      {error && (
        <p role="alert" className="border-b border-danger-ring bg-danger-soft px-5 py-2.5 text-xs text-[#FDA4AF]">
          {error}
        </p>
      )}
      {note && <p className="border-b border-line px-5 py-2.5 text-xs text-brand-400">{note}</p>}

      {(data?.data.length ?? 0) > 0 && (
        <>
          <div className="border-b border-line px-5 py-3">
            <label htmlFor="ondemand-reason" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
              Reason for settling off-cycle · required, written to the audit log
            </label>
            <input
              id="ondemand-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Merchant requested early release ahead of a supplier payment"
              className="input text-xs"
            />
          </div>

          <ul className="divide-y divide-line-subtle">
            {data!.data.map((row) => (
              <li key={row.merchant_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-content">{row.display_name ?? row.legal_name ?? row.merchant_ref}</p>
                  <p className="numeral mt-0.5 text-2xs text-content-faint">
                    {row.merchant_ref} · {row.txn_count} payment{row.txn_count === 1 ? '' : 's'} · cycle{' '}
                    {row.settlement_cycle}
                  </p>
                </div>
                <Money amount={row.net} className="text-sm text-content-muted" />
                <button
                  type="button"
                  onClick={() => settleOne.mutate(row.merchant_id)}
                  disabled={reasonTooShort || settleOne.isPending}
                  title={reasonTooShort ? 'Write a reason first' : undefined}
                  className="btn btn-secondary h-8 text-xs"
                >
                  {settleOne.isPending && settleOne.variables === row.merchant_id ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : null}
                  Settle now
                </button>
                <CustomSettle
                  merchantId={row.merchant_id}
                  merchantName={row.display_name ?? row.legal_name ?? row.merchant_ref}
                  reason={reason}
                  reasonTooShort={reasonTooShort}
                  onDone={(message) => { setError(null); setNote(message); }}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {!isLoading && (data?.data.length ?? 0) === 0 && (
        <p className="px-5 py-8 text-center text-sm text-content-muted">
          Every captured payment is already in a settlement.
        </p>
      )}
    </section>
  );
}

export function SettlementEngine() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [cycle, setCycle] = useState<'T0' | 'T1' | 'T2' | 'T3' | 'T7' | ''>('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['settlements', status],
    queryFn: () =>
      api.get<{ data: SettlementRow[]; summary: Record<string, { count: number; amount: MoneyType }> }>(
        '/settlements', { status: status || undefined },
      ),
    refetchInterval: 60_000,
  });

  const runBatch = useMutation({
    mutationFn: () => api.post<BatchResult>('/settlements/run', cycle ? { cycle } : {}),
    onSuccess: (batch) => {
      setResult(batch);
      setConfirming(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['settlements'] });
      void queryClient.invalidateQueries({ queryKey: ['settlement-queue'] });
    },
    onError: (err) => {
      setConfirming(false);
      setError(err instanceof ApiClientError ? err.message : 'The batch could not be started.');
    },
  });

  const columns: Column<SettlementRow>[] = [
    {
      key: 'ref',
      header: 'Settlement',
      render: (s) => (
        <div>
          <p className="numeral text-xs text-content">{s.settlement_ref}</p>
          <p className="numeral mt-0.5 text-2xs text-content-faint">{s.batch_id}</p>
          {/* Why it bounced, where the operator is already looking. It was
              being fetched and then shown nowhere. */}
          {s.failure_reason && <p className="mt-1 text-2xs text-danger-text">{s.failure_reason}</p>}
        </div>
      ),
    },
    {
      key: 'merchant',
      header: 'Merchant',
      render: (s) => (
        <div>
          <p className="text-xs text-content">{s.display_name}</p>
          <p className="numeral mt-0.5 text-2xs text-content-faint">
            {s.merchant_ref} · {s.cycle} · {s.transaction_count} txns
          </p>
        </div>
      ),
    },
    { key: 'gross', header: 'Gross', align: 'right', render: (s) => <Money amount={s.gross_amount} currency={s.currency} className="text-xs" /> },
    {
      key: 'reserve',
      header: 'Reserve',
      align: 'right',
      render: (s) => <Money amount={s.reserve_held} currency={s.currency} className="text-xs text-content-subtle" />,
    },
    {
      key: 'net',
      header: 'Net payout',
      align: 'right',
      render: (s) => <Money amount={s.net_payout} currency={s.currency} className="text-xs font-medium text-brand-400" />,
    },
    { key: 'status', header: 'Status', render: (s) => <StatusBadge status={s.status} /> },
    { key: 'utr', header: 'UTR', render: (s) => <UtrCell settlement={s} /> },
  ];

  return (
    <div className="space-y-4">
      <OutstandingWork summary={data?.summary} />
      <WaitingToSettle />

      {/* ---- Trigger ---------------------------------------------------- */}
      <div className="panel p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium">Run a settlement batch</h2>
            <p className="mt-1 max-w-prose text-xs text-content-muted">
              Sweeps every approved, unfrozen merchant whose captured volume has aged past its cycle. Each merchant
              settles in its own transaction — one failure never blocks the rest of the batch.
            </p>
          </div>

          <div className="flex items-end gap-2">
            <div>
              <label htmlFor="cycle" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
                Cycle
              </label>
              <select id="cycle" value={cycle} onChange={(e) => setCycle(e.target.value as typeof cycle)} className="input w-32">
                <option value="">All cycles</option>
                {['T0', 'T1', 'T2', 'T3', 'T7'].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {confirming ? (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setConfirming(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="button" onClick={() => runBatch.mutate()} className="btn btn-danger" disabled={runBatch.isPending}>
                  {runBatch.isPending ? <Spinner /> : <AlertTriangle className="h-4 w-4" />}
                  Yes, move the money
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} className="btn btn-primary">
                <PlayCircle className="h-4 w-4" />
                Run batch
              </button>
            )}
          </div>
        </div>

        {confirming && (
          <p className="mt-4 flex items-start gap-2 rounded border border-warning-ring bg-warning-soft px-3 py-2.5 text-xs text-[#FCD34D]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            This releases merchant funds and creates payout instructions{cycle ? ` for the ${cycle} cycle` : ' across every cycle'}.
            It is recorded in the audit log against your account and cannot be undone — a mistaken batch has to be
            reversed settlement by settlement.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-[#FDA4AF]">
            {error}
          </p>
        )}

        {result && (
          <div className="mt-4 rounded border border-brand-800 bg-success-soft p-3">
            <p className="text-xs text-brand-300">
              Batch <span className="numeral">{result.batch_id}</span> complete — {result.settled_count} settled
              {result.failed_count > 0 && `, ${result.failed_count} failed`} · {result.total_net.formatted} released.
            </p>
            {result.failures.length > 0 && (
              <ul className="mt-2 space-y-1">
                {result.failures.map((failure) => (
                  <li key={failure.merchantId} className="numeral text-2xs text-[#FDA4AF]">
                    {failure.merchantId}: {failure.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ---- Ledger ----------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        {['', 'PENDING', 'PROCESSING', 'PAID', 'FAILED', 'ON_HOLD'].map((value) => (
          <button
            key={value || 'ALL'}
            type="button"
            onClick={() => setStatus(value)}
            aria-pressed={status === value}
            className={`badge ${status === value ? 'badge-success' : 'badge-neutral'} cursor-pointer`}
          >
            {value ? value.replace(/_/g, ' ').toLowerCase() : 'all'}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(s) => s.settlement_ref}
        loading={isLoading}
        empty={<EmptyState title="No settlements" description="Run a batch to generate settlements for eligible merchants." />}
      />
    </div>
  );
}
